import React, { useState } from 'react';
const EmigoInteractiveIcon = ({
  onTap
}: {
  onTap?: () => void;
}) => {
  const [tapCount, setTapCount] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle, tapped, tension, pop, reset
  const [particles, setParticles] = useState([]);
  const handleTap = () => {
    if (phase === 'tension' || phase === 'pop' || phase === 'reset') return;
    const newCount = tapCount + 1;
    setTapCount(newCount);
    setPhase('tapped');
    if (onTap) onTap();
    if (newCount >= 4) {
      // Trigger sequence
      setPhase('tension');
      setTimeout(() => {
        setPhase('pop');
        generateParticles();
        setTimeout(() => {
          setPhase('reset');
          setTapCount(0);
          setTimeout(() => {
            setPhase('idle');
            setParticles([]);
          }, 1000); // Cooldown
        }, 300); // Pop duration
      }, 200); // Tension duration
    }
  };
  const generateParticles = () => {
    const newParticles = [];
    const colors = ['#E87B5C', '#FFD700', '#FFFFFF', '#E87B5C', '#FF9E80'];
    for (let i = 0; i < 30; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 80 + Math.random() * 120; // Spread distance
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;
      const isLine = Math.random() > 0.7;
      newParticles.push({
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        tx: `${tx}px`,
        ty: `${ty}px`,
        size: 4 + Math.random() * 8,
        isLine,
        rot: `${angle * (180 / Math.PI)}deg`
      });
    }
    setParticles(newParticles);
  };
  let className = 'absolute inset-0 w-full h-full object-contain z-10 rounded-full cursor-pointer transition-all duration-300 ease-out';
  let inlineStyle = {};
  if (phase === 'idle') {
    className += ' icon-idle';
  } else if (phase === 'tapped') {
    if (tapCount === 1) inlineStyle = {
      transform: 'scale(1.3) rotate(-2deg)',
      boxShadow: '0 0 20px rgba(232,123,92,0.6)'
    };
    if (tapCount === 2) inlineStyle = {
      transform: 'scale(1.6) rotate(3deg)',
      boxShadow: '0 0 30px rgba(232,123,92,0.7)'
    };
    if (tapCount === 3) inlineStyle = {
      transform: 'scale(2.0) rotate(-4deg)',
      boxShadow: '0 0 40px rgba(232,123,92,0.8)'
    };
  } else if (phase === 'tension') {
    className += ' icon-tension';
  } else if (phase === 'pop') {
    className += ' icon-pop';
  } else if (phase === 'reset') {
    inlineStyle = {
      transform: 'scale(1)',
      opacity: 1
    };
    className = 'absolute inset-0 w-full h-full object-contain z-10 rounded-full cursor-pointer transition-all duration-700 ease-in-out';
  }
  return <span className="inline-flex items-center justify-center relative w-[70px] h-[70px] md:w-[82px] md:h-[82px] lg:w-[95px] lg:h-[95px] mx-2 align-middle z-50" data-id="element-40">
      <style data-id="element-41">{`
        @keyframes idle-pulse {
          0%, 100% { transform: scale(1) translateY(0) rotate(0deg); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
          10% { transform: scale(1.12) translateY(-4px) rotate(4deg); box-shadow: 0 10px 20px -3px rgba(232, 123, 92, 0.5); }
          20% { transform: scale(1.12) translateY(-4px) rotate(-4deg); box-shadow: 0 10px 20px -3px rgba(232, 123, 92, 0.5); }
          30% { transform: scale(1) translateY(0) rotate(0deg); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
        }
        .icon-idle {
          animation: idle-pulse 4s ease-in-out infinite;
        }
        .icon-idle:hover {
          animation: none;
          transform: scale(1.15);
          box-shadow: 0 0 25px rgba(232, 123, 92, 0.6);
        }
        @keyframes tension-shake {
          0%, 100% { transform: scale(2.5) rotate(0deg); box-shadow: 0 0 40px rgba(232, 123, 92, 1); }
          25% { transform: scale(2.5) rotate(5deg); box-shadow: 0 0 40px rgba(232, 123, 92, 1); }
          50% { transform: scale(2.5) rotate(-5deg); box-shadow: 0 0 40px rgba(232, 123, 92, 1); }
          75% { transform: scale(2.5) rotate(5deg); box-shadow: 0 0 40px rgba(232, 123, 92, 1); }
        }
        .icon-tension {
          animation: tension-shake 0.1s ease-in-out infinite;
        }
        @keyframes pop-squash {
          0% { transform: scale(2.5); opacity: 1; }
          30% { transform: scale(3.2) scaleY(0.8); opacity: 1; }
          100% { transform: scale(0); opacity: 0; }
        }
        .icon-pop {
          animation: pop-squash 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes shoot-particle {
          0% { transform: translate(-50%, -50%) rotate(var(--rot)) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) rotate(var(--rot)) scale(0); opacity: 0; }
        }
      `}</style>

      {/* Particles */}
      {particles.map(p => <div key={p.id} className="absolute top-1/2 left-1/2 pointer-events-none z-0 shadow-sm" style={{
      width: p.isLine ? p.size * 3 : p.size,
      height: p.isLine ? 3 : p.size,
      borderRadius: p.isLine ? '2px' : '50%',
      backgroundColor: p.color,
      '--tx': p.tx,
      '--ty': p.ty,
      '--rot': p.rot,
      animation: 'shoot-particle 0.8s cubic-bezier(0.1, 0.8, 0.3, 1) forwards'
    }} data-id="element-42" />)}

      {/* Icon */}
      <img src="https://cdn.magicpatterns.com/uploads/kPyEtui4sL9gCL5rPK2NdZ/Emigo_Icon_(1)-modified.png" alt="Emigo app icon" onClick={handleTap} className={className + ' rounded-full'} style={inlineStyle} data-id="element-43" />
    </span>;
};
export default EmigoInteractiveIcon;